import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Label, Media, Row, UncontrolledTooltip } from "reactstrap";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import { ToastContainer } from "react-toastify";
import TranslationModal from "@/components/TranslationModal";

import productPageImg from "@/assets/img/product_page.PNG";
import categoryPageImg from "@/assets/img/category_page.PNG";
import subcategoryPageImg from "@/assets/img/subcategory_page.PNG";
import postImg from "@/assets/img/post.PNG";
import authorImg from "@/assets/img/author.PNG";
import simplePageImg from "@/assets/img/simplepage.PNG";

import FilterContainer from "./filters/FilterContainer";
import NotificationAlertOptions from "@/layouts/Alerts";
import { configCurrentSite } from "@/utils/config";

import { addPendingPage } from "@/services/pendingPagesServices/pendingPagesActions";
import { fetchPageList, setPagePublishState } from "@/services/pageServices/pagesSlice";

import { pageListParams } from "../Display/filters/Filters";

import PagesView from "./PagesView";

// SCRUM-1811: how long the search box waits for typing to stop before it asks
// the server, so a word is one request rather than one per keystroke.
const SEARCH_DEBOUNCE_MS = 300;

/**
 * SCRUM-1155: the values the columns expose to TableWrapper's global filter.
 *
 * `templateTypeOf` and `typeLabelOf` are the same strings the cells render, so
 * searching matches what the editor is actually reading. `typeLabelOf` in
 * particular covers `object_target`, which the search box this replaces never
 * looked at — a product page could not be found by its product's name.
 */
const templateNameOf = (page) => page?.page_template?.name || "-";

const templateTypeOf = (page) =>
  page?.page_template ? page.page_template?.type?.split("_")?.join(" ") || "" : "-";

const typeLabelOf = (page) => {
  const config = page?.page_configuration;
  const type = config?.type;
  if (type === "sub_category" || type === "category") {
    return `${config?.object_target?.name} (${type})`;
  }
  if (type === "product") {
    // product can be null after a product delete (soft delete) — must not crash
    return `${config?.object_target?.product?.title} (${type})`;
  }
  return type;
};

const PAGE_TYPE_IMAGES = {
  product: productPageImg,
  category: categoryPageImg,
  sub_category: subcategoryPageImg,
  post: postImg,
  author: authorImg,
};

export type PagesContainerProps = {
  /** optional: the parent's delete hook, called with the page after a delete */
  deletePageOperation?: (page: unknown) => void;
  /** passed by DisplayPagesView; this container does not read it */
  CurrentSite?: unknown;
};

/** Stable stand-in for a missing session, so effects do not rerun every render. */
const EMPTY_CONNECTED = {};

const PagesContainer = (props: PagesContainerProps) => {
  const dispatch = useAppDispatch();

  // Store selectors
  const permission = useCurrentPermission();
  // A missing session used to be a fresh `{}` on every render, so the role
  // effect and the publish callback saw a new dependency each time.
  const userconnected = useAppSelector((s) => s.auth.userconnected) || EMPTY_CONNECTED;
  const filters = useAppSelector((s) => s.pages.filters) || [];
  const list = useAppSelector((s) => s.pages.list);

  // Local state
  const [role, setRole] = useState("");
  // SCRUM-1887 (same defect as SCRUM-1885): redirect with navigate(), not a
  // `redirect` state rendered as <Navigate>. An instance that survived a quick
  // browser Back still held that state and rendered nothing.
  const navigate = useNavigate();
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [selectedPageForTranslation, setSelectedPageForTranslation] = useState(null);

  // SCRUM-1811: the table shows one page the server answered (GET /api/pages)
  // instead of slicing the site document's pages[] in the browser. The chips,
  // the search text and the page index all become query parameters, so a chip
  // or a search term holds across every page of the site (Dirk's option (b) on
  // SCRUM-1654 for the search box, option (a) on SCRUM-1811 for the chips).
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");

  // A new chip set starts at page 1 — adjusted during render, so the stale
  // page index never reaches the fetch below as a request of its own.
  const filtersKey = filters.join(",");
  const [seenFiltersKey, setSeenFiltersKey] = useState(filtersKey);
  if (seenFiltersKey !== filtersKey) {
    setSeenFiltersKey(filtersKey);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }

  useEffect(() => {
    if (search === q) return undefined;
    const timer = setTimeout(() => {
      setQ(search);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, q]);

  const params = useMemo(
    () => pageListParams({ filters, q, ...pagination }),
    // `filters` is a fresh array on every render of a mocked store; its
    // content is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filters is a new array every render; filtersKey is its content
    [filtersKey, q, pagination]
  );

  useEffect(() => {
    dispatch(fetchPageList(params));
  }, [dispatch, params]);

  // Track role (once user info available)
  useEffect(() => {
    if (userconnected?.user?.role) setRole(userconnected.user.role);
  }, [userconnected]);

  // Handlers
  const onDeletePage = useCallback(
    (page) => {
      // keep the same behavior: call the prop if parent provided it
      if (typeof props.deletePageOperation === "function") {
        props.deletePageOperation(page);
      }
    },
    [props]
  );

  const onOpenPreview = useCallback((page) => {
    if (page?.publish_statement) {
      const url = `http://${configCurrentSite.CURRENT_DOMAIN_SITE}/${page.page_configuration?.path ?? ""}`;
      window.open(url, "_blank");
    }
  }, []);

  const onConfig = useCallback((id) => {
    navigate(`/admin/update-pg/${id}`);
  }, [navigate]);

  const onEditContent = useCallback((id) => {
    navigate(`/admin/update-page-content/${id}`);
  }, [navigate]);

  const onDraftPublish = useCallback(
    async (page) => {
      const userId = userconnected?.user?.id;
      if (!userId) return;
      // SCRUM-883: toast in this screen's own context. addPendingPage is shared
      // with the editor save handlers (which toast their own messages), so a
      // global sentinel would double-toast; here we confirm / report directly.
      try {
        await dispatch(addPendingPage(page, userId));
        NotificationAlertOptions("success", "Page added to Manage Publish section");
      } catch (err) {
        NotificationAlertOptions("error", "Error publishing this page");
      }
    },
    [dispatch, userconnected]
  );

  // SCRUM-2391: publish or unpublish the page itself, also when it has no
  // Manage Publish entry (most migrated pages have none). Success is toasted
  // only after the server answered; a refusal is toasted by the error hub.
  // useCallback because it is a dependency of the `columns` useMemo below.
  const onSetPublished = useCallback(
    async (page, published) => {
      try {
        await dispatch(setPagePublishState({ id: page._id, published })).unwrap();
        NotificationAlertOptions("success", published ? "Page published" : "Page unpublished");
      } catch {
        // surfaced once by appErrorMiddleware
      }
    },
    [dispatch]
  );

  const onTranslateClick = useCallback((page) => {
    setSelectedPageForTranslation(page);
    setTranslationModalOpen(true);
  }, []);

  const toggleTranslationModal = useCallback(() => {
    setTranslationModalOpen(!translationModalOpen);
    if (translationModalOpen) {
      setSelectedPageForTranslation(null);
    }
  }, [translationModalOpen]);

  const handleTranslationComplete = useCallback((translatedPage) => {
    NotificationAlertOptions("success", "Page translated successfully!");
    // Optionally refresh the page list
    // dispatch(getSitesByDomain());
  }, []);


  /**
   * SCRUM-1155: the container only describes the columns now.
   *
   * The first four carry an `accessorFn` so TableWrapper's global filter can see
   * them; the three control columns deliberately do not, so the search never
   * matches button labels. The Tools cluster keeps its `d-flex flex-wrap`
   * wrapper and its per-row tooltip ids, and both gates on Draft Publish and
   * Delete — the role AND the publish/edit permission — stay independent.
   */
  const columns = useMemo(() => {
    const canEdit = !!(permission?.publish || permission?.edit);
    const isAdminRole =
      role === "ADMINISTRATOR" ||
      role === "CONTENTDIRECTOR" ||
      role === "CONTENTCOORDINATOR";

    const TitleCell = ({ row }) => {
      const page = row.original;
      return (
        <Media className="align-items-center">
          <a
            className="avatar rounded-circle mr-3"
            href="#picture"
            onClick={(e) => e.preventDefault()}
          >
            <img
              alt={`${page.global_title} type picture`}
              // SCRUM-1636: a page document can lack page_configuration entirely
              // (the bancompara.pt footer does), and the server hands it over as-is.
              // A bare read here threw in render and React unmounted the whole
              // admin tree — same shape as SCRUM-1453 on the Pending Pages screen.
              src={PAGE_TYPE_IMAGES[page.page_configuration?.type] || simplePageImg}
              style={{ width: "48px", height: "48px" }}
            />
          </a>
          <Media>
            <span className="mb-0 text-sm">
              <strong>{page.global_title}</strong>
            </span>
          </Media>
        </Media>
      );
    };
    TitleCell.displayName = "PageTitleCell";

    const PreviewCell = ({ row }) => {
      const page = row.original;
      return (
        <>
          <button
            className="btn-round btn-icon btn btn-primary btn-sm"
            id={`tooltip_view_${page._id}`}
            onClick={() => onOpenPreview(page)}
            disabled={!page.publish_statement}
          >
            <span className="btn-inner--icon mr-1">
              <i className="fas fa-eye" />
            </span>
            <span className="btn-inner--text">View</span>
          </button>
          <UncontrolledTooltip delay={0} placement="top" target={`tooltip_view_${page._id}`}>
            {page.publish_statement
              ? `Open http://${configCurrentSite.CURRENT_DOMAIN_SITE}/${page.page_configuration?.path ?? ""}`
              : "Preview available after publishing"}
          </UncontrolledTooltip>
        </>
      );
    };
    PreviewCell.displayName = "PagePreviewCell";

    const SetupCell = ({ row }) => {
      const page = row.original;
      return (
        <>
          <Row>
            <Label>
              <strong>
                Navigation/Footer{" "}
                {page.sub_pages ? (
                  <i className="fas fa-check text-success fa-1x" />
                ) : (
                  <i className="ni ni-fat-remove text-warning fa-1x" />
                )}
              </strong>
            </Label>
          </Row>
          <Row>
            <Label>
              <strong>
                Published{" "}
                {page.publish_statement ? (
                  <i className="fas fa-check text-success fa-1x" />
                ) : (
                  <i className="ni ni-fat-remove text-warning fa-1x" />
                )}
              </strong>
            </Label>
          </Row>
        </>
      );
    };
    SetupCell.displayName = "PageSetupCell";

    const ToolsCell = ({ row }) => {
      const page = row.original;
      return (
        <div className="d-flex flex-wrap" style={{ gap: "8px" }}>
          {/* Settings */}
          <Button
            id={`btnconfig_${page._id}`}
            color="default"
            size="sm"
            data-placement="top"
            onClick={() => onConfig(page._id)}
          >
            <i className="ni ni-settings-gear-65" />
            <span className="ml-1">Settings</span>
          </Button>
          <UncontrolledTooltip delay={0} placement="top" target={`btnconfig_${page._id}`}>
            Edit page configuration
          </UncontrolledTooltip>

          {/* Edit */}
          <Button
            id={`btnContent_${page._id}`}
            color="primary"
            size="sm"
            data-placement="top"
            onClick={() => onEditContent(page._id)}
          >
            <i className="fas fa-pen-to-square" />
            <span className="ml-1">Edit</span>
          </Button>
          <UncontrolledTooltip delay={0} placement="top" target={`btnContent_${page._id}`}>
            Edit Page Content
          </UncontrolledTooltip>

          {/* Translation */}
          <Button
            id={`btnTranslate_${page._id}`}
            color="info"
            size="sm"
            data-placement="top"
            onClick={() => onTranslateClick && onTranslateClick(page)}
          >
            <i className="ni ni-world-2" />
          </Button>
          <UncontrolledTooltip delay={0} placement="top" target={`btnTranslate_${page._id}`}>
            Translate This Page
          </UncontrolledTooltip>

          {/* SCRUM-2391: gated on the role alone, like the server route
              (PUBLISH group); an ADMINISTRATOR carries no per-site entry, so
              canEdit would hide it from exactly the users the server allows. */}
          {isAdminRole && (
            <Button
              id={`btnPublishState_${page._id}`}
              color={page.publish_statement ? "warning" : "success"}
              size="sm"
              data-placement="top"
              onClick={() => onSetPublished(page, !page.publish_statement)}
            >
              <i className={page.publish_statement ? "fas fa-eye-slash" : "fas fa-globe"} />
              <span className="ml-1">{page.publish_statement ? "Unpublish" : "Publish"}</span>
            </Button>
          )}

          {/* Admin tools */}
          {isAdminRole && canEdit && (
            <>
              <Button
                id={`btnPublish_${page._id}`}
                color="info"
                size="sm"
                data-placement="top"
                onClick={() => onDraftPublish(page)}
              >
                <i className="ni ni-curved-next mr-1" />
                Draft Publish
              </Button>

              <Button
                color="danger"
                size="sm"
                id={`btndelete_${page._id}`}
                data-placement="top"
                onClick={() => onDeletePage(page)}
              >
                <i className="fas fa-trash" />
                <span className="ml-1">Delete</span>
              </Button>
              <UncontrolledTooltip delay={0} placement="top" target={`btndelete_${page._id}`}>
                Delete This Page
              </UncontrolledTooltip>
            </>
          )}
        </div>
      );
    };
    ToolsCell.displayName = "PageToolsCell";

    return [
      {
        id: "global_title",
        header: "Meta Page Title",
        accessorFn: (page) => page?.global_title || "",
        cell: TitleCell,
      },
      {
        id: "template_name",
        header: "Template Name",
        accessorFn: templateNameOf,
        cell: ({ row }) => (
          <span>
            <strong>{templateNameOf(row.original)}</strong>
          </span>
        ),
      },
      {
        id: "template_type",
        header: "Template Type",
        accessorFn: templateTypeOf,
        cell: ({ row }) => (
          <span>
            <strong>{templateTypeOf(row.original)}</strong>
          </span>
        ),
      },
      {
        id: "type",
        header: "Type",
        accessorFn: typeLabelOf,
        cell: ({ row }) => (
          <span>
            <strong>{typeLabelOf(row.original)}</strong>
          </span>
        ),
      },
      { id: "preview", header: "Show Preview", cell: PreviewCell },
      { id: "setup", header: "Setup", cell: SetupCell },
      { id: "tools", header: "Tools", cell: ToolsCell },
    ];
  }, [
    permission,
    role,
    onOpenPreview,
    onConfig,
    onEditContent,
    onDraftPublish,
    onSetPublished,
    onDeletePage,
    onTranslateClick,
  ]);

  return (
    <>
      {/* Notifications */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      {/* Filters header (unchanged) */}
      <FilterContainer />

      {/* View */}
      <PagesView
        pages={list?.data || []}
        columns={columns}
        rowCount={list?.total || 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        searchValue={search}
        onSearchChange={setSearch}
      />
      {/* Translation Modal */}
      <TranslationModal
        isOpen={translationModalOpen}
        toggle={toggleTranslationModal}
        page={selectedPageForTranslation}
        onTranslationComplete={handleTranslationComplete}
      />
    </>
  );
};

export default PagesContainer;


